import { useCallback, useRef } from "react";

import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { PlaybackState } from "@/lib/timeline/playback";
import type { FitCastBoardContext } from "@/lib/timeline/board/FitCast/context";
import { computeTimelineHomeView } from "@/lib/timeline/view/TimelineHomeView/home";
import {
  keepUnlockedFrame,
  noteAutoZoomChange,
  seedRestoredCamera,
  toPersistedCameraFrame,
} from "@/lib/timeline/view/TimelineSession/cameraRestore";
import { resolveStageView } from "./resolveStageView";
import type { TimelineCameraFrame } from "@/lib/timeline/view/TimelineSession/session";
import type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";
import { bindLockedFollowWheelZoom } from "@/lib/timeline/view/LockedFollowWheelZoom/zoom";
import type { LockedWheelZoomHandler } from "@/lib/timeline/view/StageManualView/view";
import { applyStagePanUnlock } from "./applyStagePanUnlock";
import { useLockedCameraLayout } from "./useLockedCameraLayout";
import { useRestoredHold } from "./useRestoredHold";
import { useStageViewport } from "./useStageViewport";
import { useTimelineZoom } from "./useTimelineZoom";

export function useBookTimelineStage(
  layout: BookTimelineLayout,
  cameraLockEnabled: boolean,
  autoZoomEnabled: boolean,
  onAutoZoomOff: () => void,
  getPlayback: () => PlaybackState,
  restoredCamera: TimelineCameraFrame | null,
  onPanWhileLocked?: (next: TimelineViewTransform) => void,
  getBoardFitContext?: () => FitCastBoardContext | null,
) {
  const seed = seedRestoredCamera(restoredCamera);
  const stageRef = useRef<HTMLDivElement>(null);

  const getHomeView = useCallback((zoom?: number) => {
    const stage = stageRef.current;
    if (stage === null) {
      return null;
    }
    const w = Math.max(1, stage.clientWidth);
    const h = Math.max(1, stage.clientHeight);
    return computeTimelineHomeView(layout, w, h, w, getPlayback().playheadMs, zoom);
  }, [getPlayback, layout]);

  const viewRef = useRef<TimelineViewTransform>(seed.view);
  const followZoomRef = useRef(seed.followZoom);
  const prevCameraLockRef = useRef(cameraLockEnabled);
  const unlockFrameRef = useRef<TimelineViewTransform | null>(null);
  const panBreakRef = useRef(seed.panHeld);
  const restoredHoldRef = useRestoredHold(seed.holdUntilPlayback, layout);
  const lockedWheelZoomRef = useRef<LockedWheelZoomHandler | null>(null);
  const lockAutoFitRef = useRef(autoZoomEnabled);
  const autoZoomSeenRef = useRef(autoZoomEnabled);
  const onAutoZoomOffRef = useRef(onAutoZoomOff);
  onAutoZoomOffRef.current = onAutoZoomOff;

  const unlockFromPanRef = useRef(onPanWhileLocked);
  unlockFromPanRef.current = onPanWhileLocked;
  const lastFitMsRef = useRef(0);
  const getBoardFitRef = useRef(getBoardFitContext);
  getBoardFitRef.current = getBoardFitContext;

  const {
    zoom, panX, panY, resetToHome, resetToHomeKeepingZoom, setViewTransform,
  } = useTimelineZoom(
    stageRef,
    cameraLockEnabled,
    getHomeView,
    seed.holdUntilPlayback ? seed.view : null,
    lockedWheelZoomRef,
    () => viewRef.current,
    (next) => {
      lockAutoFitRef.current = false;
      applyStagePanUnlock(
        next, stageRef.current, viewRef, panBreakRef, restoredHoldRef,
        setViewTransform, unlockFromPanRef.current,
      );
    },
    () => { lockAutoFitRef.current = false; onAutoZoomOffRef.current(); },
    (next) => { viewRef.current = next; },
  );

  noteAutoZoomChange(
    autoZoomSeenRef, autoZoomEnabled, lockAutoFitRef,
    lastFitMsRef, restoredHoldRef, cameraLockEnabled,
  );
  keepUnlockedFrame(
    cameraLockEnabled, prevCameraLockRef.current, viewRef, unlockFrameRef,
    zoom, panX, panY,
  );

  const getViewTransform = useCallback(
    (): TimelineViewTransform => viewRef.current,
    [],
  );

  const resolveViewTransform = useCallback((): TimelineViewTransform => {
    return resolveStageView({
      layout,
      cameraLockEnabled,
      getPlayback,
      stage: stageRef.current,
      viewRef,
      followZoomRef,
      lockAutoFitRef,
      lastFitMsRef,
      panBreakRef,
      restoredHoldRef,
      getBoardFitContext: getBoardFitRef.current,
    });
  }, [cameraLockEnabled, getPlayback, layout]);

  bindLockedFollowWheelZoom(
    lockedWheelZoomRef,
    cameraLockEnabled,
    followZoomRef,
    viewRef,
    resolveViewTransform,
    setViewTransform,
    () => {
      restoredHoldRef.current = false;
      if (lockAutoFitRef.current) {
        lockAutoFitRef.current = false;
        onAutoZoomOffRef.current();
      }
    },
  );

  useLockedCameraLayout({
    cameraLockEnabled,
    viewRef,
    lastFitMsRef,
    prevCameraLockRef,
    panBreakRef,
    setViewTransform,
  });

  const viewport = useStageViewport(
    stageRef,
    cameraLockEnabled,
    seed.holdUntilPlayback,
    resetToHome,
  );

  const getViewportWidth = useCallback((): number => {
    return stageRef.current?.clientWidth ?? viewport.width;
  }, [viewport.width]);

  const getCameraFrame = useCallback((): TimelineCameraFrame => {
    const view = cameraLockEnabled ? viewRef.current : { zoom, panX, panY };
    return toPersistedCameraFrame(
      cameraLockEnabled,
      view,
      lockAutoFitRef.current,
      followZoomRef.current,
      panBreakRef.current,
    );
  }, [cameraLockEnabled, panX, panY, zoom]);

  return {
    stageRef,
    viewport,
    zoom, panX, panY,
    resetToHome, resetToHomeKeepingZoom,
    viewRef,
    getViewportWidth,
    getViewTransform,
    getCameraFrame,
    resolveViewTransform,
    cameraLockEnabled,
  };
}
